import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

// ---------------------------------------------------------------------------
// `EssentialsBuildSurface` localization — the two-tab switcher that sits above
// the page editor for the `creator` persona (`IDEShellLayout.tsx:124`).
//
// WHY THIS EXISTS: a 2026-08-18 browser reading of the authenticated Studio
// page editor under `cogo-locale=ko` found these tabs rendering `Screens` /
// `Behavior` in English while the surrounding IDE shell was Korean. The
// reading attributed them to `IDEShellLayout.tsx` (the nearest `data-testid`
// ancestor, `active-canvas-layer`); that file has no English literals and is
// already locale-wired. The literals are in `BUILD_VIEWS` in
// `essentials-build-surface.tsx`, which had no locale hook.
//
// WHY NOT REUSE `getCoogoIdeRailLabel`. That shared table already localizes
// the rail, and these two tabs point at the same two rail destinations
// (`editor`, `behavior`) — so it looks like the natural home. It is not:
// its `app.pages` key renders "Pages", while this surface deliberately says
// "Screens". Routing these tabs through it would silently rewrite the English
// from "Screens" to "Pages" — a product copy change nobody asked for, on a
// surface whose `en` this change is otherwise meant to leave untouched. The
// `behavior` tab DOES agree with `app.action` ("Behavior" / 동작 / 動作 /
// 动作 / 動作), and the Korean here is taken from that table verbatim so the
// tab and the rail cannot drift apart on the word they share.
//
// `Screens` follows the established Korean for the screen concept already in
// `coogo-ide-chrome-copy.ts` (`searchScreens: "화면 검색"`), i.e. 화면 — and
// the ja/zh/zh-TW equivalents from the same table (画面 / 页面 / 頁面).
// ---------------------------------------------------------------------------

export interface EssentialsBuildSurfaceCopy {
  /** `role="tablist"` accessible name. */
  buildWorkspace: string;
  /** Tab labels, keyed by the rail item each one activates. */
  editor: string;
  behavior: string;
}

// `en` is byte-identical to the literals this surface rendered before the
// change.
const en: EssentialsBuildSurfaceCopy = {
  buildWorkspace: "Build workspace",
  editor: "Screens",
  behavior: "Behavior",
};

const ko: EssentialsBuildSurfaceCopy = {
  buildWorkspace: "빌드 작업 영역",
  editor: "화면",
  behavior: "동작",
};

const ja: EssentialsBuildSurfaceCopy = {
  buildWorkspace: "ビルド ワークスペース",
  editor: "画面",
  behavior: "動作",
};

const zh: EssentialsBuildSurfaceCopy = {
  buildWorkspace: "构建工作区",
  editor: "页面",
  behavior: "动作",
};

const zhTW: EssentialsBuildSurfaceCopy = {
  buildWorkspace: "建置工作區",
  editor: "頁面",
  behavior: "動作",
};

const ESSENTIALS_BUILD_SURFACE_COPY: Record<
  LocaleCode,
  EssentialsBuildSurfaceCopy
> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getEssentialsBuildSurfaceCopy(
  locale: LocaleCode,
): EssentialsBuildSurfaceCopy {
  return ESSENTIALS_BUILD_SURFACE_COPY[locale] ?? en;
}
